import { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "../../../DataTable/DataTable";
import {
  MasterOption,
  MasterRow,
  ProductCatalogService,
  ProductFormRow,
} from "../../../../services/api/productCatalog.service";
import { FilterSelect, MasterPanel, StatusPill } from "./MasterPanel";

type InnerTab = "group" | "form";

const INNER_TABS: { id: InnerTab; label: string }[] = [
  { id: "group", label: "Form Group" },
  { id: "form", label: "Product Form" },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const toBool = (value: string): boolean | undefined =>
  value === "" ? undefined : value === "true";

/**
 * Product form masters.
 *
 * A form group gathers related dosage forms (Solid Oral, Liquid Oral,
 * Injectable, ...) and each form inside it is what a product's attributes point
 * at - `product_form_id` on medicine_attributes or product_attributes.
 */
export function ProductFormTab() {
  const [innerTab, setInnerTab] = useState<InnerTab>("group");
  const [activeFilter, setActiveFilter] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [groupOptions, setGroupOptions] = useState<MasterOption[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadGroups = async () => {
      const res = await ProductCatalogService.getFormGroupOptions({
        isActive: true,
      });
      if (!cancelled) setGroupOptions(res?.success ? res.data || [] : []);
    };
    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const asSelectOptions = (options: MasterOption[]) =>
    options.map((option) => ({ value: option.id, label: option.name }));

  const groupColumns: Column<MasterRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Form Group",
        width: "w-[380px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">{row.name}</span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
    ],
    []
  );

  const formColumns: Column<ProductFormRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Form",
        width: "w-[280px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">{row.name}</span>
        ),
      },
      {
        key: "formGroupName",
        title: "Form Group",
        width: "w-[280px]",
        render: (row) => (
          <span className="text-sm text-gray-700">
            {row.formGroupName || row.formGroupId}
          </span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
    ],
    []
  );

  const fetchGroups = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getFormGroups({
        page,
        pageSize,
        search,
        isActive: toBool(activeFilter),
      }),
    [activeFilter]
  );

  const fetchForms = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getForms({
        page,
        pageSize,
        search,
        formGroupId: filterGroup || undefined,
        isActive: toBool(activeFilter),
      }),
    [activeFilter, filterGroup]
  );

  const statusFilterControl = (
    <FilterSelect
      label="Status"
      value={activeFilter}
      onChange={setActiveFilter}
      options={ACTIVE_FILTER_OPTIONS}
      width="w-32"
    />
  );

  return (
    <div className="space-y-5">
      <nav className="flex flex-wrap gap-2">
        {INNER_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setInnerTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
              innerTab === tab.id
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        {innerTab === "group" && (
          <MasterPanel<MasterRow>
            title="Form Group"
            description="Groups related dosage forms, e.g. Solid Oral or Injectable."
            addLabel="Add Form Group"
            columns={groupColumns}
            searchPlaceholder="Search form groups..."
            filters={statusFilterControl}
            refreshToken={`${activeFilter}|${reloadToken}`}
            fetchRows={fetchGroups}
            fields={[
              {
                name: "name",
                label: "Name",
                type: "text",
                required: true,
                placeholder: "e.g. Solid Oral",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ name: row.name, isActive: row.isActive })}
            onCreate={async (values) => {
              const res = await ProductCatalogService.createFormGroup({
                name: values.name,
                isActive: values.isActive,
              });
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
            onUpdate={async (row, values) => {
              const res = await ProductCatalogService.updateFormGroup(
                String(row.id),
                { name: values.name, isActive: values.isActive }
              );
              if (res?.success) setReloadToken((token) => token + 1);
              return res;
            }}
          />
        )}

        {innerTab === "form" && (
          <MasterPanel<ProductFormRow>
            title="Product Form"
            description="The dosage form a product is supplied in - Tablet, Syrup, Cream and so on."
            addLabel="Add Product Form"
            columns={formColumns}
            searchPlaceholder="Search product forms..."
            refreshToken={`${activeFilter}|${filterGroup}|${reloadToken}`}
            fetchRows={fetchForms}
            filters={
              <>
                <FilterSelect
                  label="Form Group"
                  value={filterGroup}
                  onChange={setFilterGroup}
                  options={[
                    { value: "", label: "All" },
                    ...asSelectOptions(groupOptions),
                  ]}
                />
                {statusFilterControl}
              </>
            }
            fields={[
              {
                name: "name",
                label: "Form Name",
                type: "text",
                required: true,
                placeholder: "e.g. Tablet",
              },
              {
                name: "formGroupId",
                label: "Form Group",
                type: "select",
                required: true,
                options: asSelectOptions(groupOptions),
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({
              name: row.name,
              formGroupId: row.formGroupId,
              isActive: row.isActive,
            })}
            onCreate={(values) =>
              ProductCatalogService.createForm({
                name: values.name,
                formGroupId: values.formGroupId,
                isActive: values.isActive,
              })
            }
            onUpdate={(row, values) =>
              ProductCatalogService.updateForm(String(row.id), {
                name: values.name,
                formGroupId: values.formGroupId,
                isActive: values.isActive,
              })
            }
          />
        )}
      </div>
    </div>
  );
}
